/// Pure logic behind the static landing page (`index.html` + `main.ts`).
/// Kept dependency-free (no DOM, no fetch) so it's plain-vitest-testable the
/// same way the Cloud Functions `*Core.ts` files are — `main.ts` is the only
/// place that touches `document`/`fetch`/`localStorage`.

import { formatCounterValue } from '../util/formatCounter'
import type { CounterValue, TowerLogPayload } from '../data/towerLog'

export { formatCounterValue }

// ─── Tower Log strip (4 hero tiles) ────────────────────────────────

/// The four counters the landing page's Tower Log strip shows, in display
/// order. A deliberate subset of the full /towerlog grid — see
/// `TowerLogPage.tsx` for the complete counter set.
export const TOWERLOG_STRIP_COUNTER_IDS = [
  'landings',
  'hoursOnWatch',
  'passengersDelivered',
  'towersRepresented',
] as const

export interface PickedCounter {
  id: string
  label: string
  value: number
  formatted: string
}

/// Selects + formats the strip's 4 counters from a `getTowerLog` payload.
/// Tolerates a missing/non-numeric counter or missing label by skipping that
/// tile entirely (never renders a placeholder) — the strip itself stays
/// hidden by the caller when this returns an empty array.
export function pickCounters(payload: TowerLogPayload | null | undefined): PickedCounter[] {
  if (!payload || !payload.available || !payload.counters || !payload.labels) return []
  const counters = payload.counters
  const labels = payload.labels
  const out: PickedCounter[] = []
  for (const id of TOWERLOG_STRIP_COUNTER_IDS) {
    const value: CounterValue | undefined = counters[id]
    const label = labels[id]
    if (typeof value !== 'number' || typeof label !== 'string' || label.length === 0) continue
    out.push({ id, label, value, formatted: formatCounterValue(value) })
  }
  return out
}

// ─── Board feed (Latest from the tower) ────────────────────────────

/// Shape returned by the `getBoardFeed` Cloud Function
/// (`functions/src/boardFeedCore.ts`'s `FeedItem`, read across the
/// client/functions TypeScript-project boundary as plain JSON).
export interface BoardFeedItem {
  id: string
  title: string
  snippet: string
  createdAt: string
  commentCount: number
  authorIsDeveloper: boolean
  upCount: number
}

export interface BoardFeedResponse {
  items: BoardFeedItem[]
}

export interface LandingFeedItem {
  id: string
  title: string
  snippet: string
  /// Short "Jul 18" style label, not a true relative ("2 days ago") date.
  dateLabel: string
  commentCount: number
  authorIsDeveloper: boolean
}

/// `createdAt` -> "Jul 18" (UTC, so the result is deterministic regardless
/// of the reader's or test runner's local timezone). Falls back to the
/// empty string for an unparseable/missing date rather than throwing —
/// callers should treat that as "omit the date", not fail the whole item.
export function formatFeedDateLabel(createdAtIso: string): string {
  const d = new Date(createdAtIso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/// The newest 3 board-feed entries, mapped to what the landing page renders.
/// Tolerates a malformed/missing payload by returning an empty array (the
/// caller keeps the "Latest from the tower" card hidden in that case).
export function feedItems(json: BoardFeedResponse | null | undefined): LandingFeedItem[] {
  if (!json || !Array.isArray(json.items)) return []
  return json.items.slice(0, 3).map((item) => ({
    id: item.id,
    title: item.title,
    snippet: item.snippet,
    dateLabel: formatFeedDateLabel(item.createdAt),
    commentCount: typeof item.commentCount === 'number' ? item.commentCount : 0,
    authorIsDeveloper: item.authorIsDeveloper === true,
  }))
}

// ─── SPA-param forwarding ───────────────────────────────────────────

/// Mirrors the inline `<script>` shim at the top of `index.html`'s `<head>`
/// (kept there, not imported, so it can run before any module script even
/// starts loading) — exported here purely so the exact same condition is
/// unit-testable. Stripe checkout returns land on
/// `/?purchase=success&session_id=...` and the private dashboard is
/// `/?dashboard`; deep level links use `/?level=<id>`. All three must
/// forward to `/play` rather than render the landing page.
export function shouldForwardToPlay(search: string): boolean {
  const q = new URLSearchParams(search)
  return q.has('dashboard') || q.has('purchase') || q.has('level')
}
